/**
 * apiCall — une requête de `data/` dont le moteur attend la réponse.
 *
 * Elle passe par `apiRequest` : les actions `_REQUEST` / `_SUCCESS` / `_FAILURE` partent comme pour
 * tout le legacy (les réducteurs qui rangent l'élève, l'activité courante, la todo s'en servent), le
 * jeton, le 401 et le 419 sont les siens. Trois différences, voulues pour un moteur : la boîte « Pas de
 * connexion Internet » ne s'ouvre pas (`offlineDialog: false`, l'échec hors ligne arrive à l'appelant,
 * qui le montre) ; l'état du réseau est relu avant l'envoi (`freshNetworkState` : après une coupure,
 * sur le web, l'état gardé par NetInfo peut rester « hors ligne », et plus rien ne partait) ; la réponse
 * revient en promesse (`onResult`). La tranche d'API est remise au repos une fois lue : un écran
 * legacy monté plus tard ne rejoue pas une réponse qui n'était pas la sienne.
 */
import { apiIdle, apiRequest } from "../../data/redux/actions/api"

/** Les options d'une requête du moteur, pour les appels qui passent `apiRequest` sans `apiCall`. */
export const ENGINE_REQUEST_OPTIONS = { offlineDialog: false, freshNetworkState: true } as const

/**
 * Les options d'une requête qu'un écran legacy partage avec le moteur : la boîte hors ligne quand
 * l'appelant la veut ; sans elle, l'état du réseau relu, comme les requêtes du moteur.
 */
export const requestOptionsFor = (offlineDialog: boolean) => ({ offlineDialog, freshNetworkState: !offlineDialog })

/** L'échec tel que le réducteur d'API le range (`{ ...response.data, status }`, ou hors ligne). */
export type ApiFailure = {
    status?: number | null
    message?: string | null
    isOffline?: boolean
    errors?: Record<string, string[]> | null
}

type Dispatch = (action: never) => unknown

export type ApiService = ((...args: never[]) => Promise<unknown>) & { name: string }

/** L'échec d'une requête : ce que le réducteur d'API a rangé (`{ ...response.data, status }`). */
export class ApiCallError extends Error {
    readonly status: number | null
    readonly failure: ApiFailure
    constructor(service: string, failure: ApiFailure | null | undefined) {
        const status = typeof failure?.status === "number" ? failure.status : null
        super(
            failure?.isOffline ? `${service}: offline` : `${service} failed${status != null ? ` (HTTP ${status})` : ""}`
        )
        this.name = "ApiCallError"
        this.status = status
        this.failure = failure ?? {}
    }
}

/** L'échec rangé d'une requête `apiCall`, ou `null` pour toute autre cause. */
export function apiFailureOf(cause: unknown): ApiFailure | null {
    return cause instanceof ApiCallError ? cause.failure : null
}

export type ApiCallParams = {
    routeParams?: Record<string, unknown>
    data?: unknown
    urlParams?: Record<string, unknown>
    /**
     * La réponse entière, pas son `data` : la messagerie rend ses ressources sans enveloppe, avec
     * leur `meta` (la page suivante d'un fil). Défaut `false`.
     */
    whole?: boolean
}

export function apiCall<T = unknown>(dispatch: Dispatch, service: ApiService, params: ApiCallParams = {}): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        dispatch(
            apiRequest(service, params.routeParams ?? {}, params.data ?? {}, params.urlParams ?? {}, null, null, {
                ...ENGINE_REQUEST_OPTIONS,
                onResult: (result: { ok: boolean; data?: T; allData?: T; error?: ApiFailure }) => {
                    dispatch(apiIdle(service) as never)
                    if (result.ok) resolve((params.whole === true ? result.allData : result.data) as T)
                    else reject(new ApiCallError(service.name, result.error))
                },
            }) as never
        )
    })
}
